import api from './api';

/**
 * Mood API Service
 * 
 * Handles all mood-related API calls including:
 * - Submitting mood for analysis
 * - Getting mood history
 * - Managing mood data
 */

/**
 * Submit a mood for analysis and get song recommendations
 * @param {string} mood - User's mood description
 * @returns {Promise<Object>} Response containing mood analysis and track recommendations
 */
export const postMood = async (mood) => {
  try {
    const response = await api.post('/mood', { mood });
    return response.data;
  } catch (error) {
    console.error('Error posting mood:', error);
    
    // Provide user-friendly error messages
    if (error.response?.status === 400) {
      throw new Error('Invalid mood input. Please try again.');
    } else if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please check your connection.');
    } else {
      throw new Error('Failed to process mood. Please try again.');
    }
  }
};

/**
 * Submit a mood with real-time progress updates via Server-Sent Events
 * @param {string} mood - User's mood description
 * @param {Function} onProgress - Callback for progress updates (stage, description)
 * @returns {Promise<Object>} Response containing mood analysis and track recommendations
 */
export const postMoodWithProgress = async (mood, onProgress) => {
  return new Promise((resolve, reject) => {
    const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const url = `${baseURL}/api/mood/stream`;
    
    // Create a POST request with Server-Sent Events
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    // Add authorization header if token exists
    const token = localStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    
    let buffer = '';
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 3 || xhr.readyState === 4) {
        buffer += xhr.responseText;
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep the last incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'progress') {
                onProgress(data.stage, data.description);
              } else if (data.type === 'mood_analysis') {
                onProgress('mood_analysis', data.data);
              } else if (data.type === 'complete') {
                resolve(data.result);
              } else if (data.type === 'error') {
                reject(new Error(data.error));
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', parseError);
            }
          }
        }
      }
    };
    
    xhr.onerror = () => {
      reject(new Error('Network error occurred'));
    };
    
    xhr.ontimeout = () => {
      reject(new Error('Request timeout'));
    };
    
    xhr.timeout = 120000; // 2 minutes timeout
    
    xhr.send(JSON.stringify({ mood }));
  });
};

/**
 * Get user's mood history
 * @returns {Promise<Array>} Array of mood objects with associated tracks
 */
export const getMoodHistory = async () => {
  try {
    const response = await api.get('/history');
    return response.data;
  } catch (error) {
    console.error('Error fetching mood history:', error);
    
    if (error.response?.status === 401) {
      throw new Error('Please log in to view your mood history.');
    } else {
      throw new Error('Failed to load mood history. Please try again.');
    }
  }
};

/**
 * Get a specific mood by ID
 * @param {number} moodId - ID of the mood to retrieve
 * @returns {Promise<Object>} Mood object with tracks
 */
export const getMoodById = async (moodId) => {
  try {
    const response = await api.get(`/mood/${moodId}`);
    return response.data.mood;
  } catch (error) {
    console.error('Error fetching mood:', error);
    
    if (error.response?.status === 404) {
      throw new Error('Mood not found.');
    } else if (error.response?.status === 401) {
      throw new Error('Please log in to view this mood.');
    } else {
      throw new Error('Failed to load mood. Please try again.');
    }
  }
};

/**
 * Delete a mood and all associated tracks
 * @param {number} moodId - ID of the mood to delete
 * @returns {Promise<boolean>} True if deletion was successful
 */
export const deleteMood = async (moodId) => {
  try {
    const response = await api.delete(`/mood/${moodId}`);
    return response.data.success;
  } catch (error) {
    console.error('Error deleting mood:', error);
    
    if (error.response?.status === 404) {
      throw new Error('Mood not found or already deleted.');
    } else if (error.response?.status === 401) {
      throw new Error('Please log in to delete moods.');
    } else {
      throw new Error('Failed to delete mood. Please try again.');
    }
  }
};

/**
 * Get more songs based on existing mood analysis
 * @param {Object} moodDescriptions - Existing mood analysis
 * @param {Array} currentTracks - Current tracks to avoid repetition
 * @param {number|null} moodId - Mood ID to save new tracks to database
 * @returns {Promise<Object>} Response containing new tracks
 */
export const getMoreSongs = async (moodDescriptions, currentTracks, moodId = null) => {
  try {
    const response = await api.post('/mood/more-songs', {
      moodDescriptions,
      currentTracks,
      moodId
    });
    return response.data;
  } catch (error) {
    console.error('Error getting more songs:', error);
    
    if (error.response?.status === 400) {
      throw new Error('Invalid request. Please try again.');
    } else if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please check your connection.');
    } else {
      throw new Error('Failed to get more songs. Please try again.');
    }
  }
};

/**
 * Delete a specific track from a mood
 * @param {number} trackId - ID of the track to delete
 * @returns {Promise<boolean>} True if deletion was successful
 */
export const deleteTrack = async (trackId) => {
  try {
    const response = await api.delete(`/track/${trackId}`);
    return response.data.success;
  } catch (error) {
    console.error('Error deleting track:', error);
    
    if (error.response?.status === 404) {
      throw new Error('Track not found or already deleted.');
    } else if (error.response?.status === 401) {
      throw new Error('Please log in to delete tracks.');
    } else {
      throw new Error('Failed to delete track. Please try again.');
    }
  }
};